import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Loader2, Lock, Save, Workflow } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '../../../../../../components/ui';
import { FeatureGate } from '../../../../../../components/FeatureGate';
import { FlowEditor } from '../../../../../../match-flow/editor/FlowEditor';
import { trpc } from '../../../../../../lib/trpc';

import type { FlowDefinition } from '@rivaled/shared';

export const Route = createFileRoute('/app/clubs/$slug/queues/$queueId/flow-editor')({
  component: FlowEditorPage,
});

function FlowEditorPage() {
  const { slug, queueId } = Route.useParams();
  const navigate = useNavigate();

  const { data: myClubs } = trpc.club.getMyClubs.useQuery();
  const membership = myClubs?.find((c) => c.slug === slug);
  const clubId = membership?.id;
  const isAdmin = membership?.role === 'owner' || membership?.role === 'admin';

  const { data: queue, isLoading: queueLoading } = trpc.queue.get.useQuery({ queueId });

  // Load existing flow definition if the queue has one
  const { data: flowDefinition, isLoading: flowLoading } = trpc.matchFlow.get.useQuery(
    { flowId: queue?.matchFlowDefinitionId ?? '', clubId: clubId ?? '' },
    { enabled: !!queue?.matchFlowDefinitionId && !!clubId }
  );

  // List existing flow definitions for the club
  const { data: flowDefinitions } = trpc.matchFlow.list.useQuery(
    { clubId: clubId ?? '' },
    { enabled: !!clubId }
  );

  const utils = trpc.useUtils();
  const [isSaving, setIsSaving] = useState(false);

  // Create new flow definition
  const createMutation = trpc.matchFlow.create.useMutation({
    onSuccess: (data) => {
      utils.matchFlow.list.invalidate();
      // Associate with queue
      assignFlowToQueue(data.id);
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to create flow');
      setIsSaving(false);
    },
  });

  // Update existing flow definition
  const updateMutation = trpc.matchFlow.update.useMutation({
    onSuccess: () => {
      toast.success('Flow saved');
      utils.matchFlow.get.invalidate();
      utils.matchFlow.list.invalidate();
      setIsSaving(false);
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to save flow');
      setIsSaving(false);
    },
  });

  // Update queue to use the flow definition
  const updateQueueMutation = trpc.queue.update.useMutation({
    onSuccess: () => {
      toast.success('Flow saved and assigned to queue');
      utils.queue.get.invalidate({ queueId });
      setIsSaving(false);
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to assign flow to queue');
      setIsSaving(false);
    },
  });

  const assignFlowToQueue = (flowId: string) => {
    if (!clubId) return;
    updateQueueMutation.mutate({
      queueId,
      clubId,
      matchFlowDefinitionId: flowId,
    });
  };

  const handleSave = (flow: FlowDefinition) => {
    if (!clubId) return;
    setIsSaving(true);

    if (queue?.matchFlowDefinitionId && flowDefinition) {
      // Update existing flow
      updateMutation.mutate({
        flowId: queue.matchFlowDefinitionId,
        clubId,
        flowData: flow,
      });
    } else {
      // Create new flow
      createMutation.mutate({
        clubId,
        name: `${queue?.name ?? 'Queue'} Flow`,
        description: `Match flow for ${queue?.name ?? 'this queue'}`,
        flowData: flow,
      });
    }
  };

  // Access check - redirect if not admin
  if (!queueLoading && (!isAdmin || !clubId)) {
    navigate({ to: '/app/clubs/$slug/queues/$queueId', params: { slug, queueId } });
    return null;
  }

  if (queueLoading || !queue) {
    return (
      <div className="h-full flex items-center justify-center bg-surface-900">
        <Loader2 className="size-8 animate-spin text-surface-400" />
      </div>
    );
  }

  // Wait for flow to load if queue has one
  if (queue.matchFlowDefinitionId && flowLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-surface-900">
        <Loader2 className="size-8 animate-spin text-surface-400" />
      </div>
    );
  }

  return (
    <FeatureGate
      clubId={clubId!}
      slug={slug}
      feature="hasVisualEditor"
      fallback={
        <div className="h-full flex flex-col items-center justify-center bg-surface-900 p-6 text-center">
          <div className="w-16 h-16 rounded-2xl bg-surface-800 border border-surface-700 flex items-center justify-center mb-4">
            <Lock className="size-8 text-surface-500" />
          </div>
          <h1 className="text-2xl font-display font-bold text-white mb-2">
            Visual Flow Editor
          </h1>
          <p className="text-surface-400 max-w-md mb-6">
            The visual flow editor is available on Competitive tier and above.
            Upgrade your club to create custom match flows.
          </p>
          <div className="flex gap-3">
            <Link to="/app/clubs/$slug/queues/$queueId" params={{ slug, queueId }}>
              <Button variant="ghost">
                <ArrowLeft className="size-4 mr-2" />
                Back to Queue
              </Button>
            </Link>
            <Link to="/app/clubs/$slug/settings" params={{ slug }} search={{ section: 'billing' }}>
              <Button variant="primary">Upgrade Plan</Button>
            </Link>
          </div>
        </div>
      }
    >
      <div className="h-full flex flex-col bg-surface-900 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-surface-700 bg-surface-800 shrink-0">
          <div className="flex items-center gap-3">
            <Link to="/app/clubs/$slug/queues/$queueId" params={{ slug, queueId }}>
              <Button variant="ghost" size="sm" className="!px-2">
                <ArrowLeft className="size-4" />
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <Workflow className="size-5 text-brand-400" />
              <div>
                <h1 className="text-lg font-display font-bold text-white">Match Flow Editor</h1>
                <p className="text-xs text-surface-400">{queue.name}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {isSaving && (
              <span className="text-xs text-surface-400 flex items-center gap-1.5">
                <Loader2 className="size-3.5 animate-spin" />
                Saving...
              </span>
            )}
          </div>
        </div>

        {/* Editor */}
        <div className="flex-1 min-h-0">
          <FlowEditor
            clubId={clubId!}
            queueId={queueId}
            flowId={queue.matchFlowDefinitionId ?? undefined}
            initialFlow={flowDefinition?.flowData as FlowDefinition | undefined}
            onSave={handleSave}
          />
        </div>
      </div>
    </FeatureGate>
  );
}
